/**
 * Текущий тариф компании в продукте для бокового меню и настроек — из GET /v1/accounts/{id}.
 * Кабинет биллинга открыт только владельцу компании (и сотрудникам Kindata): остальным тариф не показываем.
 */
import {reactive} from 'vue'
import {BILLING_PRODUCT} from "./config";
import {getCompanyId} from "./host";
import {getAccount} from "./api";
import {formatDate} from "./format";

const DAY = 24 * 60 * 60 * 1000
// За сколько дней предупреждать, что подписка закончится
const EXPIRING_DAYS = 3

let pending = null

export const tariffSummary = reactive({
    companyId: null,
    loaded: false,
    // Нет прав на кабинет биллинга (не владелец компании)
    forbidden: false,
    error: '',
    // Подписка с продуктом BILLING_PRODUCT: платная, триал или бесплатный тариф (id = null); null — тарифа нет
    subscription: null,

    async load(force = false) {
        const company_id = await getCompanyId()
        if (!company_id) {
            return this
        }
        if (!force && this.loaded && this.companyId === company_id) {
            return this
        }
        if (!pending) {
            pending = this.fetch(company_id).finally(() => {
                pending = null
            })
        }
        await pending
        return this
    },

    async fetch(company_id) {
        try {
            const overview = await getAccount(company_id)
            this.subscription = overview.subscriptions.find((item) => item.products.includes(BILLING_PRODUCT)) || null
            this.forbidden = false
            this.error = ''
        } catch (e) {
            this.subscription = null
            this.forbidden = e.status === 403
            this.error = this.forbidden ? '' : e.message
        }
        this.companyId = company_id
        this.loaded = true
    },
})

function daysLeft(subscription, now) {
    return Math.ceil((new Date(subscription.period_end).getTime() - now) / DAY)
}

/**
 * Что показать о тарифе: название, строка статуса, её тон и действие.
 * @returns {{name: string, note: string, tone: string, action: ({text: string, to: Object}|null), active: boolean}}
 *   tone — muted | info | warning | danger
 */
export function describeTariff(subscription, now = Date.now()) {
    const plans = {name: 'billing-plans'}
    if (!subscription) {
        return {name: 'Не подключён', note: 'Тариф не подключён', tone: 'danger', active: false,
            action: {text: 'Выбрать тариф', to: plans}}
    }
    const name = subscription.plan.name
    if (subscription.is_free) {
        return {name, note: 'Бесплатный тариф', tone: 'muted', active: true, action: {text: 'Улучшить', to: plans}}
    }
    const until = formatDate(subscription.period_end)
    const left = daysLeft(subscription, now)
    if (subscription.status === 'past_due') {
        return {name, note: 'Подписка не оплачена', tone: 'danger', active: true,
            action: {text: 'Оплатить', to: {name: 'billing'}}}
    }
    if (subscription.status === 'trialing') {
        return {name, note: `Пробный период до ${until}`, tone: left <= EXPIRING_DAYS ? 'warning' : 'info', active: true,
            action: left <= EXPIRING_DAYS ? {text: 'Выбрать тариф', to: plans} : null}
    }
    if (subscription.status === 'active') {
        // Закончится, если продления не будет: отменена или автопродления нет
        const ending = subscription.cancel_at_period_end || !subscription.auto_renew
        if (ending && left <= EXPIRING_DAYS) {
            return {name, note: `Заканчивается ${until}`, tone: 'warning', active: true,
                action: {text: 'Продлить', to: {name: 'billing-checkout',
                    query: {plan: subscription.plan.id, period: subscription.period_months}}}}
        }
        return {name, note: `До ${until}`, tone: 'muted', active: true, action: null}
    }
    return {name, note: 'Подписка закончилась', tone: 'danger', active: false, action: {text: 'Продлить', to: plans}}
}
